import React, { Component } from 'react';
import { ActionStatus, GamePlayerStatus, TurnStatus } from '../../../../../Constants';
import AddGhost2d from './AddGhost';
import AntiJuice2d from './AntiJuice';
import { GhostActionJuice, GhostActionTarget, RemoveGhosts2d } from './GhostingController';
import { AddLodging2d, LodgingActionJuice, LodgingActionTarget } from './LodgingController';
import ReceiveSpecialGuest2d from './ReceiveSpecialGuest2d';
import ScoreChoice from './ScoringController';
import { VanguardGivesLodging } from './VanguardGivesLodging';
import ActionChooser from './ActionChooser';
import ActionSelector from './ActionSelector';
import DraftSelector from './DraftSelector';
import ResearchController from './ResearchController';
import RenovationController from './RenovationController';


// Window-level signals from dice overlays + EventRouter. We can't import
// from those modules here without a circular dep, so we read the flags
// directly. See FateDieOverlay / RandomActionDieOverlay (flag) and
// EventRouter (queue inspector).
const DIE_SETTLED_EVENT = 'die-settled';
const DIE_ROLL_STARTED_EVENT = 'die-roll-started';
// Watchdog if dice events never arrive (e.g. transient state hiccup).
// Long enough to cover a Random Action Die roll AND its follow-up Fate
// Die juice roll plus settle holds.
const DICE_PENDING_WATCHDOG_MS = 15000;
const POST_SETTLE_GRACE_MS = 600;


function diceInFlight() {
    if (window.__dieRolling) return true;
    if (typeof window.__eventRouterHasPendingDice === 'function') {
        try { return !!window.__eventRouterHasPendingDice(); }
        catch (err) {  // eslint-disable-line no-unused-vars
            return false;
        }
    }
    return false;
}


function isJuiceState(props) {
    if (!props || !props.gamePlayerData) return false;
    const status = props.gamePlayerData.status;
    const actionStatus = props.gamePlayerData.action_status;
    if (status === GamePlayerStatus.LODGING_ID
            && actionStatus === ActionStatus.WAIT_FOR_JUICE) return true;
    if (status === GamePlayerStatus.GHOSTING_ID
            && actionStatus === ActionStatus.WAIT_FOR_JUICE) return true;
    if (status === GamePlayerStatus.DEFENDING_ID) return true;
    return false;
}


export default class ActionRouter extends Component {

    constructor(props) {
        super(props);
        // Start pending only if dice are already visibly rolling on
        // mount. Reload-mid-juice should NOT hide the UI indefinitely.
        this.state = { dicePending: diceInFlight() };
        this.watchdogTimer = null;
        this.settleTimer = null;
    }

    componentDidMount() {
        window.addEventListener(DIE_SETTLED_EVENT, this.onDieSettled);
        window.addEventListener(DIE_ROLL_STARTED_EVENT, this.onDieRollStarted);
        if (this.state.dicePending) this.startWatchdog();
    }

    componentDidUpdate(prevProps) {
        // Transition INTO a juice state: assume dice events are en
        // route. Hide the controller UI until die-settled fires (or
        // the watchdog expires). gamePlayerData arriving before the
        // queued dice events is the leak we're closing.
        const wasJuice = isJuiceState(prevProps);
        const nowJuice = isJuiceState(this.props);
        if (!wasJuice && nowJuice && !this.state.dicePending) {
            this.setState({ dicePending: true });
            this.startWatchdog();
        }
    }

    componentWillUnmount() {
        window.removeEventListener(DIE_SETTLED_EVENT, this.onDieSettled);
        window.removeEventListener(
            DIE_ROLL_STARTED_EVENT, this.onDieRollStarted);
        if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
        if (this.settleTimer) clearTimeout(this.settleTimer);
    }

    startWatchdog = () => {
        if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
        this.watchdogTimer = setTimeout(() => {
            this.watchdogTimer = null;
            if (this.state.dicePending) {
                this.setState({ dicePending: false });
            }
        }, DICE_PENDING_WATCHDOG_MS);
    }

    onDieRollStarted = () => {
        if (this.settleTimer) {
            clearTimeout(this.settleTimer);
            this.settleTimer = null;
        }
        if (!this.state.dicePending) {
            this.setState({ dicePending: true });
        }
        this.startWatchdog();
    }

    onDieSettled = () => {
        if (this.settleTimer) clearTimeout(this.settleTimer);
        // Give a follow-up die in a sequence (Random Action Die →
        // Fate Die for the resulting action's juice) time to register.
        this.settleTimer = setTimeout(() => {
            this.settleTimer = null;
            if (diceInFlight()) return;  // another die mounted in the gap
            if (this.watchdogTimer) {
                clearTimeout(this.watchdogTimer);
                this.watchdogTimer = null;
            }
            if (this.state.dicePending) {
                this.setState({ dicePending: false });
            }
        }, POST_SETTLE_GRACE_MS);
    }

    render() {
        if (this.state.dicePending) return <></>;

        const gamePlayerStatus = this.props.gamePlayerData.status;
        const actionStatus = this.props.gamePlayerData.action_status;
        const turnStatus = this.props.gamePlayerData.turn_status;

        switch(gamePlayerStatus) {
        case GamePlayerStatus.RESEARCHING_ID:
            switch (actionStatus) {
            case ActionStatus.WAIT_ID:
                return <ResearchController {...this.props}/>
            default:
                return <></>
            }
        case GamePlayerStatus.LODGING_ID:
            switch (actionStatus) {
            case ActionStatus.WAIT_FOR_TARGET_ID:
                return <LodgingActionTarget {...this.props}/>
            case ActionStatus.WAIT_FOR_JUICE:
                return <LodgingActionJuice {...this.props}/>
            case ActionStatus.ADD_ID:
                return <AddLodging2d {...this.props}/>
            default:
                return <></>
            }
        case GamePlayerStatus.RENOVATING_ID:
            switch (actionStatus) {
            case ActionStatus.WAIT_ID:
                return <RenovationController {...this.props}/>
            default:
                return <></>
            }
        case GamePlayerStatus.ADVERTISING_ID:
            switch (actionStatus) {
            case ActionStatus.WAIT_ID:
                // AdvertisementPossibilities (the AC) is now a singleton
                // mounted once on Dashboard, gating its dispatches
                // internally on ADVERTISING_ID + WAIT_ID. Mobile sees the
                // identical dispatches from that single instance, so this
                // sub-case mounts nothing.
                return <></>
            case ActionStatus.ADD_ID:
                return <VanguardGivesLodging {...this.props}/>
            default:
                return <></>
            }
        case GamePlayerStatus.GHOSTING_ID:
            switch (actionStatus) {
            case ActionStatus.WAIT_FOR_TARGET_ID:
                return <GhostActionTarget {...this.props}/>
            case ActionStatus.WAIT_FOR_JUICE:
                return <GhostActionJuice {...this.props}/>
            case ActionStatus.REMOVE_ID:
                return <RemoveGhosts2d {...this.props}/>
            default:
                return <></>
            }
        case GamePlayerStatus.GHOSTED_ID:
        case GamePlayerStatus.GHOST_STARTER_ID:
            // 2026-05-16: GHOST_STARTER_ID is supposed to be a
            // transient state during Ghost Wave setup, but seats can
            // get stuck there with attacking_ghosts > 0. Backend now
            // routes GHOST_STARTER_ID clicks through
            // message_while_ghosted just like GHOSTED_ID, so render
            // the dock picker for both.
            return <AddGhost2d {...this.props}/>
        case GamePlayerStatus.PICK_DRAFT_ID:
            return <DraftSelector {...this.props}/>
        case GamePlayerStatus.SCORING_ID:
            return <ScoreChoice {...this.props}/>
        case GamePlayerStatus.TAKING_TURN_ID:
            switch (turnStatus) {
            case TurnStatus.SELECT_ACTION_ID:
                return <ActionSelector {...this.props}/>
            case TurnStatus.OFF_ID:
                return <>
                    <ActionChooser {...this.props}/>
                    <ActionSelector {...this.props}/>
                </>
            case TurnStatus.CHOOSE_ACTIONS_ID:
                return <>
                    <ActionChooser {...this.props}/>
                    <ActionSelector {...this.props}/>
                </>
            case TurnStatus.END_TURN_ID:
                return <>
                    <ActionChooser {...this.props}/>
                    <ActionSelector {...this.props}/>
                </>
            default:
                return <></>
            }
        case GamePlayerStatus.SPECIAL_ID:
            return <ReceiveSpecialGuest2d {...this.props}/>
        case GamePlayerStatus.DEFENDING_ID:
            return <AntiJuice2d {...this.props}/>
        case GamePlayerStatus.WAITING_ID:
            return <>
                <ActionChooser {...this.props}/>
                <ActionSelector {...this.props}/>
            </>
        default:
            // console.log('No Action.')
            return <></>
        }
    }
}
